import { useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { History, Pencil, Plus } from 'lucide-react'
import DashboardFrame from '@/frontend/features/DashboardRuntime/DashboardFrame'
import { ContentsSwitcher, HostFilters, PageStepper, usePageKeys } from '@/frontend/features/DashboardRuntime/DashboardContents'
import ViewEditor from '@/frontend/features/DashboardRuntime/ViewEditor'
import ViewHistoryPanel from '@/frontend/features/DashboardRuntime/ViewHistoryPanel'
import type { DashboardContext, FrameNavigation, Publication, ViewCommand, ViewConfiguration, ViewHistory } from '@/frontend/features/DashboardRuntime/types'

interface Draft { name: string; config: ViewConfiguration; base: string; viewId?: string; revision?: number }

// The page on screen lives in this page's URL (?page=), so a link or a reload opens it again.
function urlPage(): string | null {
  return new URLSearchParams(window.location.search).get('page')
}
function writeUrlPage(page: string | null) {
  const url = new URL(window.location.href)
  if (page) url.searchParams.set('page', page)
  else url.searchParams.delete('page')
  if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url)
}

export default function DashboardWorkbench({ publication, projectId, path, selectedView, history, copy }: {
  publication: Publication; projectId: string; path: string; selectedView?: string; history?: ViewHistory; copy: Record<string, string>
}) {
  const pages = publication.schema.pages ?? []
  const [selected, setSelected] = useState(selectedView ?? '')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [command, setCommand] = useState<ViewCommand>()
  const [ready, setReady] = useState(false)
  const [busy, setBusy] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [initialPage] = useState(() => { const page = urlPage(); return page && pages.some(item => item.id === page) ? page : null })
  const [page, setPage] = useState<string | null>(initialPage ?? pages[0]?.id ?? null)
  const [filters, setFilters] = useState<ViewConfiguration['filters']>({})
  const [navigation, setNavigation] = useState<FrameNavigation>()
  const current = useRef<DashboardContext>({ page_id: null, filters: {} })
  const views = publication.saved_views ?? []
  const view = views.find(item => item.id === selected)
  const blank = (): ViewConfiguration => ({ page_id: pages[0]?.id ?? '', filters: {}, hidden_cards: [], card_order: [], visuals: {} })
  const apply = (configuration: ViewConfiguration | null, applyFilters = true) => setCommand(previous => ({ configuration, apply_filters: applyFilters, sequence: (previous?.sequence ?? 0) + 1 }))
  useEffect(() => { setSelected(selectedView ?? '') }, [selectedView])
  // Remote updates refresh the selected view, but never replace an editor draft.
  // The page chosen in the contents wins over the view's initial page.
  useEffect(() => {
    if (draft) return
    const configuration = view?.compatible ? view.configuration : blank()
    apply({ ...configuration, page_id: page ?? configuration.page_id })
  }, [selected, view?.revision, publication.version, Boolean(draft)])
  useEffect(() => { setReady(false) }, [publication.version])
  // The preview follows presentation edits (type, colors, axes, hide, order) at once; filters wait for Preview.
  useEffect(() => {
    if (draft) apply({ ...draft.config, page_id: page ?? draft.config.page_id }, false)
  }, [draft?.config.visuals, draft?.config.hidden_cards, draft?.config.card_order])
  useEffect(() => { if (!publication.access) setDraft(null) }, [Boolean(publication.access)])
  useEffect(() => { writeUrlPage(page) }, [page])

  const go = (pageId: string, sectionId: string | null = null, nextFilters = filters) => {
    setPage(pageId)
    setNavigation(previous => ({ page_id: pageId, section_id: sectionId, filters: nextFilters, sequence: (previous?.sequence ?? 0) + 1 }))
  }
  const changeFilters = (next: ViewConfiguration['filters']) => {
    setFilters(next)
    if (page) go(page, null, next)
  }
  // The frame reports what it shows (a saved view or a link inside it may change the page); the contents follow.
  const onContext = (context: DashboardContext) => {
    current.current = context
    if (context.page_id && context.page_id !== page) setPage(context.page_id)
    setFilters(context.filters as ViewConfiguration['filters'])
  }
  const capture = (config: ViewConfiguration): ViewConfiguration => ({ ...config,
    page_id: current.current.page_id ?? config.page_id, filters: current.current.filters as ViewConfiguration['filters'] })
  const start = (duplicate: boolean) => {
    setShowHistory(false)
    setDraft({ name: view ? `${view.name}${duplicate ? ` ${copy.copy_suffix}` : ''}` : copy.new_view,
      config: capture(structuredClone(view?.compatible ? view.configuration : blank())), base: publication.version!,
      viewId: duplicate ? undefined : view?.id, revision: view?.revision })
  }
  const conflict = !!draft && (draft.base !== publication.version || (!!draft.viewId && views.find(item => item.id === draft.viewId)?.revision !== draft.revision))
  const save = () => {
    if (!draft || conflict) return
    setBusy(true)
    router.visit(`${path}/views${draft.viewId ? `/${draft.viewId}` : ''}`, { method: draft.viewId ? 'patch' : 'post',
      data: { name: draft.name, configuration: draft.config, base_version: draft.base, expected_revision: draft.revision },
      preserveScroll: true, preserveState: true,
      onSuccess: next => { if (!(next.props.flash as { alert?: string } | undefined)?.alert) setDraft(null) },
      onFinish: () => setBusy(false) })
  }
  const multiPage = pages.length > 1
  usePageKeys(multiPage ? pages : [], page, id => go(id))
  return <div className="@container flex min-h-0 flex-1 flex-col">
    <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2 md:px-6">
      <select aria-label={copy.saved_views} className="max-w-64 rounded-md border bg-background px-3 py-1.5 text-sm" value={selected} disabled={!!draft} onChange={event => { setSelected(event.target.value); setShowHistory(false) }}>
        <option value="">{copy.original_view}</option>{views.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select>
      {view && <span className="text-xs text-muted-foreground">v{view.revision}</span>}
      {publication.access && !draft && <>
        <button type="button" disabled={!ready} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs hover:bg-muted disabled:opacity-40" onClick={() => start(true)}><Plus className="size-3.5" />{view ? copy.duplicate_view : copy.save_as_view}</button>
        {view?.compatible && <button type="button" disabled={!ready} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs hover:bg-muted" onClick={() => start(false)}><Pencil className="size-3.5" />{copy.edit_view}</button>}
      </>}
      {view && !draft && <button type="button" className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs hover:bg-muted" onClick={() => { setShowHistory(true); router.get(path, { saved_view: view.id, history_view: view.id }, { only: ['view_history'], preserveState: true, preserveScroll: true }) }}><History className="size-3.5" />{copy.history}</button>}
      {draft && <span className="text-xs text-muted-foreground">{copy.preview_notice}</span>}
    </div>
    {view && !view.compatible && <p role="alert" className="border-b bg-amber-50 px-4 py-2 text-sm text-amber-900">{copy.view_outdated}</p>}
    {showHistory && history && view && history.view_id === view.id && <ViewHistoryPanel history={history} view={view} path={path} canEdit={!!publication.access && view.compatible} onClose={() => setShowHistory(false)} copy={copy} />}
    <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-1.5 md:px-6">
          {multiPage && <ContentsSwitcher pages={pages} current={page} onSelect={go} copy={copy} />}
          {multiPage && <PageStepper pages={pages} current={page} onSelect={id => go(id)} copy={copy} />}
        </div>
        {(publication.schema.filters ?? []).some(spec => !spec.pages?.length || (page && spec.pages.includes(page))) &&
          <div className="flex flex-wrap items-center gap-2 border-b px-4 py-1.5 md:px-6">
            <HostFilters specs={publication.schema.filters ?? []} page={page} values={filters} onChange={changeFilters} copy={copy} />
          </div>}
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <DashboardFrame publication={publication} projectId={projectId} path={path} command={command} navigation={navigation} initialPage={initialPage}
            onReady={() => setReady(true)} onContext={onContext} />
          {draft && publication.access && <ViewEditor publication={publication} name={draft.name} config={draft.config} setName={name => setDraft({ ...draft, name })}
            setConfig={config => setDraft({ ...draft, config })} onCapture={() => setDraft({ ...draft, config: capture(draft.config) })}
            onPreview={() => apply(draft.config)} onCancel={() => setDraft(null)} onSave={save} conflict={conflict} busy={busy} copy={copy} />}
        </div>
      </div>
    </div>
  </div>
}
